import { gql, useMutation, useQuery } from '@apollo/client';
import IconLink from '@apollo/icons/default/IconLink.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
  Switch,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback } from 'react';
import {
  Link as ReactRouterLink,
  Route,
  Switch as RouteSwitch,
} from 'react-router-dom';
import * as Yup from 'yup';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { HeadersEditor } from 'src/components/headersEditor/HeadersEditor';
import { NewHeaderButton } from 'src/components/headersEditor/NewHeaderButton';
import { SelectWithLabel } from 'src/components/support-request/SupportRequest';
import { URLField } from 'src/components/urlField/URLField';
import { useAriaDescribedBySetting } from 'src/hooks/useAriaDescribedBySetting';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isValidUrl } from 'src/lib/isValidUrl';

import { useHeadersManagerContext } from '../../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { GraphQLSubscriptionLibraryChoice } from '../../hooks/useExplorerState/useSubscriptions';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../../hooks/usePerGraphIdentifierLocalStorage';

const SetVariantURLMutation = gql`
  mutation Graph__SetVariantURLMutation(
    $graph: ID!
    $variant: String!
    $url: String
    $subscriptionUrl: String
  ) {
    service(id: $graph) {
      variant(name: $variant) {
        updateURL(url: $url) {
          id
          url
        }
        updateSubscriptionURL(subscriptionUrl: $subscriptionUrl) {
          id
          subscriptionUrl
        }
      }
    }
  }
`;

const SetSendCookiesMutation = gql`
  mutation Graph__SetSendCookiesMutation(
    $graph: ID!
    $variant: String!
    $sendCookies: Boolean!
  ) {
    service(id: $graph) {
      variant(name: $variant) {
        updateSendCookies(sendCookies: $sendCookies) {
          id
          sendCookies
        }
      }
    }
  }
`;
/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  url: Yup.string().test(
    'valid-http-url',
    'Please enter a valid URL',
    (value: string) => {
      return !value || isValidUrl(value);
    },
  ),
  subscriptionUrl: Yup.string().test(
    'valid-http-or-ws-subscription-url',
    'Please enter a valid URL',
    (value: string) => {
      return (
        !value ||
        isValidUrl(value, { protocols: ['ws', 'wss', 'http', 'https'] })
      );
    },
  ),
  protocol: Yup.string()
    .oneOf([
      'auto-detect',
      'http-multipart',
      'graphql-ws',
      'subscriptions-transport-ws',
    ])
    .required(),
  sendCookies: Yup.boolean().required(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

interface Props {
  isOpen: boolean;
  graphRef: GraphRef;
  /**
   * Callback called when form is submitted
   */
  onCompleted: () => void;
  /**
   * Callback called when we attempt to close the modal
   */
  onClose: () => void;
}

export const DEFAULT_DEPLOYED_WS_URL = 'ws://localhost:4000'; // nosemgrep
export const DEFAULT_DEPLOYED_HTTP_URL = 'http://localhost:4000';

/**
 * Modal to add, remove, or edit the URL of a schema variant
 *
 * Loading state is handled by formik.
 */
export const RegistryConnectionSettingsModal = ({
  isOpen,
  graphRef,
  onClose,
  onCompleted,
}: Props) => {
  const { data, loading, error } = useQuery<
    GraphQLTypes.RegistryConnectionSettingsModalQuery,
    GraphQLTypes.RegistryConnectionSettingsModalQueryVariables
  >(
    gql`
      query RegistryConnectionSettingsModalQuery(
        $graphId: ID!
        $graphVariant: String!
      ) {
        service(id: $graphId) {
          id
          title
          graphType
          schemaTag(tag: $graphVariant) {
            id
            variant {
              id
              name
              url
              subscriptionUrl
              sendCookies
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef.graphId,
        graphVariant: graphRef.graphVariant,
      },
    },
  );

  const [setVariantURLs, { error: setVariantURLsError }] = useMutation<
    GraphQLTypes.Graph__SetVariantURLMutation,
    GraphQLTypes.Graph__SetVariantURLMutationVariables
  >(SetVariantURLMutation, { onCompleted });

  const [setVariantSendCookies, { error: setSendCookiesError }] = useMutation<
    GraphQLTypes.Graph__SetSendCookiesMutation,
    GraphQLTypes.Graph__SetSendCookiesMutationVariables
  >(SetSendCookiesMutation, { onCompleted });

  const graph = data?.service;
  const variant = graph?.schemaTag?.variant;
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [protocol, setProtocol] = usePerGraphIdentifierLocalStorage({
    key: 'graphqlSubscriptionProtocol',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: 'auto-detect',
  });

  // If the user is on a cloud-hosted supergraph they should not be able to
  // edit the endpoint and websocket urls of the current variant.
  const isCloudSupergraph =
    graph?.graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH;

  const {
    errors,
    values,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik<FormValues>({
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      url: variant?.url ?? '',
      subscriptionUrl: variant?.subscriptionUrl ?? '',
      protocol,
      sendCookies: !!variant?.sendCookies,
    },
    onSubmit: async (formValues) => {
      if (!isCloudSupergraph) {
        await setVariantURLs({
          variables: {
            graph: graphRef.graphId,
            variant: graphRef.graphVariant,
            // If this is empty, change it to `null` to indicate that we're
            // clearing the value
            url: formValues.url || null,
            subscriptionUrl: formValues.subscriptionUrl || null,
          },
        });
      }
      await setVariantSendCookies({
        variables: {
          graph: graphRef.graphId,
          variant: graphRef.graphVariant,
          sendCookies: formValues.sendCookies,
        },
      });
      setProtocol?.(formValues.protocol as GraphQLSubscriptionLibraryChoice);
      setSharedHeaderDefinitions(newSharedHeaders);
    },
    validationSchema,
  });

  const { sharedHeaderDefinitions, setSharedHeaderDefinitions } =
    useHeadersManagerContext();

  const [newSharedHeaders, setNewSharedHeaders] = React.useState(
    sharedHeaderDefinitions,
  );
  React.useEffect(() => {
    if (sharedHeaderDefinitions) {
      setNewSharedHeaders(sharedHeaderDefinitions);
    }
  }, [sharedHeaderDefinitions]);

  const describedBySetting = useAriaDescribedBySetting(
    'connection-setting-description-',
  );

  const handleOnChangeEndpointUrl = useCallback(
    (href: string) => setFieldValue('url', href),
    [setFieldValue],
  );

  const handleOnChangeSubscriptionUrl = useCallback(
    (href: string) => setFieldValue('subscriptionUrl', href),
    [setFieldValue],
  );

  const handleOnChangeGraphProtocol = useCallback(
    (newProtocol: string) => setFieldValue('protocol', newProtocol),
    [setFieldValue],
  );

  if (!loading && (error || !variant)) {
    onClose();
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <Form onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Connection settings</ModalTitle>
            <ModalDescription>
              Update the connection settings for{' '}
              <b>
                {graph?.title ?? graphRef.graphId}
                <> / {variant?.name ?? graphRef.graphVariant}</>
              </b>
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <div className="flex justify-center">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                {isCloudSupergraph ? (
                  <div>
                    <label className="mb-2 text-base font-semibold">
                      Endpoint
                    </label>
                    <div className="flex items-center p-2.5 text-base">
                      <IconLink className="mr-2" />
                      <Link
                        isExternal
                        className="truncate"
                        to={values.url ?? DEFAULT_DEPLOYED_HTTP_URL}
                      >
                        {values.url ?? DEFAULT_DEPLOYED_HTTP_URL}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <FormControl
                    isInvalid={!!setVariantURLsError || !!errors.url}
                  >
                    <FormLabel>Endpoint</FormLabel>
                    <URLField
                      autoFocus
                      isDisabled={isSubmitting}
                      name="url"
                      onBlur={handleBlur}
                      onChange={handleOnChangeEndpointUrl}
                      placeholder={DEFAULT_DEPLOYED_HTTP_URL}
                      value={values.url}
                      isSocket={false}
                    />
                    <FormErrorMessage>{errors.url}</FormErrorMessage>
                  </FormControl>
                )}
                {isCloudSupergraph ? (
                  <div>
                    <label className="mb-2 text-base font-semibold">
                      Subscription Endpoint
                    </label>
                    <div className="flex items-center p-2.5 text-base">
                      <IconLink className="mr-2" />
                      <Link
                        className="truncate"
                        isExternal
                        to={values.subscriptionUrl ?? DEFAULT_DEPLOYED_HTTP_URL}
                      >
                        {values.subscriptionUrl ?? DEFAULT_DEPLOYED_HTTP_URL}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <FormControl className="w-80">
                      <FormLabel>Subscriptions</FormLabel>
                      <Input
                        name="subscriptionUrl"
                        onBlur={handleBlur}
                        onChange={(event) =>
                          handleOnChangeSubscriptionUrl(event.target.value)
                        }
                        placeholder={DEFAULT_DEPLOYED_WS_URL}
                        value={values.subscriptionUrl}
                        leftElement={<IconLink />}
                      />

                      {/* nosemgrep */}
                      {values.subscriptionUrl.startsWith('ws://') &&
                      !(
                        'ws://localhost'.startsWith(values.subscriptionUrl) ||
                        'ws://127.0.0.1'.startsWith(values.subscriptionUrl)
                      ) ? (
                        <FormHelperText iconType="warning">
                          Most browsers disallow secure websites making requests
                          {/* nosemgrep */}
                          over insecure protocols such as ws:// - consider using
                          the encrypted wss:// instead
                        </FormHelperText>
                      ) : undefined}
                      <FormErrorMessage>
                        {errors.subscriptionUrl}
                      </FormErrorMessage>
                    </FormControl>

                    <SelectWithLabel
                      onBlur={handleBlur}
                      matchTriggerWidth={false}
                      placement="bottom-end"
                      className="ml-3 w-44"
                      truncate={true}
                      disabled={isSubmitting}
                      renderTriggerNode={(selectedItem) =>
                        selectedItem?.value || protocol
                      }
                      onChange={(event) => {
                        handleOnChangeGraphProtocol(event.target.value);
                      }}
                      value={values.protocol}
                      label={<FormLabel>Implementation</FormLabel>}
                    >
                      <option
                        className="h-auto"
                        value={
                          'auto-detect' as GraphQLSubscriptionLibraryChoice
                        }
                      >
                        <div>Auto-detect</div>
                        <div className="flex flex-col opacity-50">
                          Automatically determines which websocket protocol to
                          use
                        </div>
                      </option>
                      <option
                        className="h-auto"
                        value={'graphql-ws' as GraphQLSubscriptionLibraryChoice}
                      >
                        <div>graphql-ws</div>
                        <div className="flex flex-col opacity-50">
                          Implements the "graphql-transport-ws" protocol
                        </div>
                      </option>
                      <option
                        className="h-auto"
                        value={
                          'subscriptions-transport-ws' as GraphQLSubscriptionLibraryChoice
                        }
                      >
                        <div>subscriptions-transport-ws</div>
                        <div className="flex flex-col opacity-50">
                          Implements the "graphql-ws" protocol
                        </div>
                      </option>
                      <option className="h-auto" value="http-multipart">
                        <div>Multipart subscriptions</div>
                        <div className="flex flex-col opacity-50">
                          {
                            // TODO add a link to docs when ready
                          }
                          Implements Apollo's multipart subscription protocol
                        </div>
                      </option>
                    </SelectWithLabel>
                  </div>
                )}
                <div>
                  <div>
                    <FormControl className="flex flex-row justify-between">
                      <FormLabel>Include cookies</FormLabel>
                      <Switch
                        isChecked={values.sendCookies}
                        onChange={(e) =>
                          setFieldValue('sendCookies', e.target.checked)
                        }
                        aria-describedby={describedBySetting}
                      >
                        {values.sendCookies ? 'ON' : 'OFF'}
                      </Switch>
                    </FormControl>
                  </div>

                  <div
                    className="mt-2 text-sm text-secondary"
                    aria-hidden
                    id={describedBySetting}
                  >
                    To use cookies in requests
                    <ol>
                      <li className="mt-2">
                        The cookie's value must contain{' '}
                        <Link
                          to="https://www.chromium.org/updates/same-site"
                          isExternal
                        >
                          SameSite=None; Secure
                        </Link>
                      </li>
                      <li className="mt-2">
                        These CORS headers need to be present in your server's
                        response to Studio:
                        <div className="mt-2 break-words rounded bg-secondary p-2 font-mono">
                          Access-Control-Allow-Origin:
                          https://studio.apollographql.com
                          <br />
                          Access-Control-Allow-Credentials: true
                        </div>
                      </li>
                    </ol>
                  </div>
                </div>
                <div className="font-semibold">Shared headers</div>
                <Alert status="warning">
                  <strong>
                    Do not include sensitive data in shared headers.
                  </strong>{' '}
                  To include sensitive data (such as an access token) in your
                  operations, instead use environment variables, which are not
                  sent to or stored by Apollo.{' '}
                  <Link
                    isExternal
                    to="https://www.apollographql.com/docs/studio/explorer/#environment-variables"
                  >
                    Learn more in the docs
                  </Link>
                  .
                </Alert>
                <div>
                  <HeadersEditor
                    headerDefinitions={newSharedHeaders}
                    setHeaderDefinitions={setNewSharedHeaders}
                    withCheckbox={false}
                    // TODO - the HeadersEditor component is confusingly used in Studio.
                    // It was made for the Explorer which shows both shared & tab headers.
                    // It is abused here to show just shared headers. Passing shared headers
                    // in headerDefinitions & nothing to `sharedHeaders`. This component
                    // should be split up so we can pass props that make sense for just
                    // shared headers
                    headersDisabled={false}
                    sharedHeadersDisabled={false}
                  />
                  {!newSharedHeaders.length && (
                    <div className="text-sm text-secondary">
                      No shared headers set
                    </div>
                  )}
                  <NewHeaderButton
                    className="mt-2"
                    isSharedHeader={true}
                    onClick={() =>
                      setNewSharedHeaders(
                        newSharedHeaders.concat({
                          headerName: '',
                          value: '',
                          checked: true,
                          key: Math.random(),
                        }),
                      )
                    }
                  />
                </div>
                {setVariantURLsError && (
                  <ErrorMessage error={setVariantURLsError} />
                )}
                {setSendCookiesError && (
                  <ErrorMessage error={setSendCookiesError} />
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <RouteSwitch>
              {/* Don't show graph settings link when opened from graph settings */}
              <Route
                path={graphSettingsRouteConfig.definition}
                render={() => null}
              />
              {variant?.name && (
                <Route
                  render={() => (
                    <Button
                      variant="hidden"
                      onClick={onClose}
                      as={ReactRouterLink}
                      to={graphSettingsRouteConfig.path({
                        graphId: graphRef.graphId,
                        graphVariant: variant.name,
                      })}
                      leftIcon={<IconSettings />}
                    >
                      Graph settings
                    </Button>
                  )}
                />
              )}
            </RouteSwitch>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={isSubmitting}
                onClick={onClose}
                type="button"
              >
                {loading || variant?.url || variant?.subscriptionUrl
                  ? 'Cancel'
                  : 'Not yet'}
              </Button>
              {!loading && (
                <Button
                  variant="primary"
                  loadingText="Save"
                  isLoading={isSubmitting}
                  type="submit"
                >
                  Save
                </Button>
              )}
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
